import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';

import { EmptyData } from '~/components/EmptyData';
import { FullScreenLoader } from '~/components/FullScreenLoader';
import { Pagination } from '~/components/Pagination';
import { ROUTES } from '~/constants/routes';
import {
  useGetCompanyHistoryChangeQuery,
  useGetCompanyHistoryQuery,
} from '~/services/rtkQuery/company';
import { getCurrentPageInfo, getTotalPagesCount } from '~/utils/pagination';

import styles from './styles.module.scss';

const FETCH_LIMIT = 10;
const DATE_TIME_FORMAT = 'MMM dd, yyyy HH:mm';

interface Props {
  companyId: number;
}

const Component: React.FC<Props> = ({ companyId }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const getCompanyHistoryQuery = useGetCompanyHistoryQuery({
    id: companyId,
    pageNum: currentPage,
    perPage: FETCH_LIMIT,
  });
  const [expandedHistoryChangeId, setExpandedHistoryChangeId] = useState<number | null>(null);
  const getCompanyHistoryChangeQuery = useGetCompanyHistoryChangeQuery(
    { id: expandedHistoryChangeId || 0 },
    { skip: !expandedHistoryChangeId },
  );

  const toggleExpandedHistory = useCallback((id: number) => {
    setExpandedHistoryChangeId(prevValue => (prevValue === id ? null : id));
  }, []);

  const handleViewReport = useCallback(
    (id: number) => {
      navigate(generatePath(ROUTES.COMPANY, { id: id.toString() }));
    },
    [navigate],
  );

  const getChangeWithParsedDate = useCallback((change: string) => {
    const dateRegex = /\b\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\b/g;

    return change.replace(dateRegex, match => {
      const formattedMatch = format(new Date(`${match} UTC`), DATE_TIME_FORMAT);

      return formattedMatch;
    });
  }, []);

  const totalPages = useMemo(() => {
    return getTotalPagesCount({
      totalItemsCount: getCompanyHistoryQuery.currentData?.result.total || 0,
      fetchLimit: FETCH_LIMIT,
    });
  }, [getCompanyHistoryQuery.currentData?.result]);

  if (
    getCompanyHistoryQuery.isFetching ||
    !getCompanyHistoryQuery.currentData ||
    getCompanyHistoryQuery.currentData.result.data.length === 0
  ) {
    return (
      <EmptyData
        description="No history available"
        loading={getCompanyHistoryQuery.isFetching}
        title="History"
      />
    );
  }

  return (
    <div className={styles.container}>
      <table className={styles.container__table}>
        <thead>
          <tr>
            <th>
              <Typography color="text.primary" fontWeight="300" lineHeight="16px" variant="body1">
                Date & Time
              </Typography>
            </th>
            <th>
              <Typography color="text.primary" fontWeight="300" lineHeight="16px" variant="body1">
                Changed by
              </Typography>
            </th>
            <th>
              <Typography color="text.primary" fontWeight="300" lineHeight="16px" variant="body1">
                Changed
              </Typography>
            </th>
            <th>
              <Typography color="text.primary" fontWeight="300" lineHeight="16px" variant="body1">
                Historical Reports
              </Typography>
            </th>
          </tr>
        </thead>
        <tbody>
          {getCompanyHistoryQuery.currentData?.result.data.map((change, index, changes) => (
            <Fragment key={change.id}>
              <tr>
                <td>
                  <Typography
                    color="text.primary"
                    fontWeight="500"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {format(new Date(`${change.dateTime} UTC`), DATE_TIME_FORMAT)}
                  </Typography>
                </td>
                <td>
                  <Typography
                    color="text.primary"
                    fontWeight="500"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {change.user ? `${change.user.firstName} ${change.user.lastName}` : 'N/A'}
                  </Typography>
                </td>
                <td>
                  <Typography
                    color="text.primary"
                    fontWeight="500"
                    lineHeight="16px"
                    variant="body1"
                  >
                    {change.changed}
                  </Typography>
                </td>
                <td>
                  <div>
                    <button
                      disabled={!change.id || change.id === companyId}
                      type="button"
                      onClick={() => handleViewReport(change.id || 0)}
                    >
                      <Typography
                        color="var(--main-2-color)"
                        fontWeight="500"
                        lineHeight="16px"
                        sx={
                          !change.id || change.id === companyId
                            ? undefined
                            : { textDecorationLine: 'underline' }
                        }
                        variant="body1"
                      >
                        {!change.id || change.id === companyId ? '-' : 'View Report'}
                      </Typography>
                    </button>
                    {!(currentPage === totalPages && index === changes.length - 1) && change.id && (
                      <button
                        aria-label="Changes History"
                        title="Changes History"
                        type="button"
                        onClick={() => toggleExpandedHistory(change.id || 0)}
                      >
                        {change.id === expandedHistoryChangeId ? (
                          <RemoveIcon sx={{ color: 'var(--black-color)' }} />
                        ) : (
                          <AddIcon sx={{ color: 'var(--gray-2-color)' }} />
                        )}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {change.id === expandedHistoryChangeId &&
                getCompanyHistoryChangeQuery.currentData?.result.data.map((changeItem, index) => (
                  <tr key={index}>
                    <td aria-label="Change" className={styles['container__expanded-table-cell']} />
                    <td aria-label="Change" className={styles['container__expanded-table-cell']} />
                    <td className={styles['container__expanded-table-cell']}>
                      <Typography color="text.primary" lineHeight="16px" variant="body1">
                        {getChangeWithParsedDate(changeItem)}
                      </Typography>
                    </td>
                    <td aria-label="Change" className={styles['container__expanded-table-cell']} />
                  </tr>
                ))}
            </Fragment>
          ))}
        </tbody>
      </table>
      <div className={styles.container__footer}>
        <Typography
          color="var(--gray-12-color)"
          fontWeight="500"
          lineHeight="14px"
          variant="subtitle2"
        >
          {getCurrentPageInfo({
            currentPage,
            fetchLimit: FETCH_LIMIT,
            currentPageItemsCount: getCompanyHistoryQuery.currentData?.result.data.length || 0,
            totalItemsCount: getCompanyHistoryQuery.currentData?.result.total || 0,
          })}
        </Typography>
        <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />
      </div>
      <FullScreenLoader loading={getCompanyHistoryChangeQuery.isFetching} />
    </div>
  );
};

export const CompanyHistoryChanges = memo(Component);
